import type { BotWidgetConfig } from './types'

// Docked (non-floating) sidebar width. Shared with BotWidget.ts's
// syncBodyPadding() so the host page is pushed over by exactly the panel width.
export const SIDEBAR_WIDTH_PX = 440

// Timing for the floating ⇄ sidebar morph (BotWidget.morphViewMode) and the host
// page's body padding that follows it, so the panel and the page it pushes over
// move together as one motion.
export const VIEW_MODE_MORPH_MS = 450
export const VIEW_MODE_MORPH_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)'

// Total length of the launcher pill's first-appearance intro (.intro below) — the
// gradient ring, its longest piece, starts at 0.2s and runs 3s. BotWidget removes
// the class after this so later hovers/show-hides aren't affected.
export const LAUNCHER_INTRO_MS = 3300

// Ported 1:1 from AIChatBotWidget.js's createStyle() template string. Kept as a
// runtime-injected <style> tag (not a bundled stylesheet) deliberately: this
// script embeds into arbitrary third-party host pages via <script src>, which
// have no build pipeline of their own to pull a CSS file through.
// config.launcherStyle → declarations for the launcher pill. Characters that could
// close the declaration or the rule are stripped so a host value can't spill into
// the rest of the stylesheet.
function buildLauncherOverrides(style: Record<string, string> | undefined): string {
  if (!style) return ''
  const declarations = Object.entries(style)
    .map(([key, value]) => {
      const property = key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`).replace(/[^a-z-]/g, '')
      const safeValue = String(value).replace(/[;{}<>]/g, '').replace(/!important/gi, '').trim()
      return property && safeValue ? `${property}: ${safeValue} !important;` : ''
    })
    .filter(Boolean)
    .join('\n    ')
  if (!declarations) return ''
  // Out-ranks .bot-launcher-primary(:hover) — !important included — while leaving
  // the .disabled look alone.
  return `
  .bot-launcher-group:not(.disabled) .bot-launcher-primary,
  .bot-launcher-group:not(.disabled) .bot-launcher-primary:hover {
    ${declarations}
  }`
}

export function buildWidgetStyles(config: BotWidgetConfig): string {
  const isSecondVersion = config.botVersion?.toLowerCase()?.includes('v2') ?? false
  const isTaxBandits = config.product?.toLowerCase()?.includes('taxbandits') ?? false
  // Brand pair for the launcher sparkle and greeting card accents — TaxBandits'
  // orange/blue unless the host passes its own.
  const primaryColor = config.primaryColor || 'rgb(242, 109, 33)'
  const secondaryColor = config.secondaryColor || 'rgb(48, 151, 243)'
  // Greeting input's gradient ring (deep blue → blue → orange), matching chatbot-ui's
  // --chat-ring-* defaults; the deep start tone is mixed from a host secondaryColor.
  const ringStart = config.secondaryColor ? `color-mix(in srgb, ${secondaryColor} 80%, #000)` : '#0a77d6'
  const ringMid = secondaryColor
  const ringEnd = primaryColor
  const windowTransitions = [
    'width',
    'height',
    'max-width',
    'max-height',
    'top',
    'bottom',
    'left',
    'right',
    'border-radius',
    'transform',
    'opacity',
  ]
    .map((property) => `${property} ${VIEW_MODE_MORPH_MS}ms ${VIEW_MODE_MORPH_EASING}`)
    .join(',\n      ')

  return `
  .bot-widget-container {
    position: fixed;
    bottom: 30px;
    right: 20px;
    z-index: 999999;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }

  /* Launcher position (config.launcherPosition, or wherever the visitor dragged it)
     — BotWidget.applyLauncherAnchor() sets the inline left/right/top/bottom offsets
     and these data attributes. Left-anchored: the stack hugs the left edge. Top-
     anchored: the stack flips so the launcher sits nearest its corner and the
     greeting/AI-assistant cards open downward, away from it. */
  .bot-widget-container[data-anchor-x="left"] {
    align-items: flex-start;
  }
  .bot-widget-container[data-anchor-x="left"] .bot-launcher-group {
    align-items: flex-start;
  }
  .bot-widget-container[data-anchor-x="left"] .bot-greeting {
    transform-origin: bottom left;
  }
  .bot-widget-container[data-anchor-y="top"] {
    flex-direction: column-reverse;
  }
  .bot-widget-container[data-anchor-y="top"] .bot-greeting {
    margin: 16px 0 0;
  }

  /* config.isEnableLauncherDrag: the launcher pills double as the drag handle (see
     bindLauncherDrag()), with the grip as the visual cue. touch-action: none lets a
     touch drag move the launcher instead of scrolling the page. */
  .bot-launcher-group.draggable {
    position: relative;
    touch-action: none;
  }
  /* Grip sits just outside the pill, on the side facing into the page (left of
     it by default, right of it when left-anchored). Hidden until the launcher is
     hovered or being dragged, and never shown while the launcher is disabled
     (drag is blocked then too — see bindLauncherDrag()). It stays inside
     .bot-launcher-group, so hovering the grip itself keeps it visible and a
     press on it still starts a drag. */
  .drag-indicator {
    position: absolute;
    top: 50%;
    right: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 28px;
    margin-right: 4px;
    border-radius: 6px;
    background: #fff;
    color: #17191e;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
    opacity: 0;
    visibility: hidden;
    transform: translateY(-50%);
    cursor: grab;
    transition:
      opacity 0.15s ease-in-out,
      visibility 0s linear 0.15s;
  }
  .drag-indicator::after {
    /* Bridges the gap to the pill so the pointer can reach the grip without
       leaving the hover area. */
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 100%;
    width: 4px;
  }
  .bot-widget-container[data-anchor-x="left"] .drag-indicator {
    right: auto;
    left: 100%;
    margin-right: 0;
    margin-left: 4px;
  }
  .bot-widget-container[data-anchor-x="left"] .drag-indicator::after {
    left: auto;
    right: 100%;
  }
  .bot-launcher-group.draggable:hover .drag-indicator,
  .bot-widget-container.dragging .drag-indicator {
    opacity: 1;
    visibility: visible;
    transition: opacity 0.15s ease-in-out;
  }
  .bot-launcher-group.disabled .drag-indicator {
    display: none;
  }
  .bot-widget-container.dragging,
  .bot-widget-container.dragging * {
    cursor: grabbing !important;
    user-select: none;
  }
  .bot-widget-container.dragging .bot-launcher-btn {
    transform: none;
  }

  /* Closed-state launcher — two stacked pills replacing the old single circular
     toggle button. Hidden by default (nothing to show before BotReady, matching
     the old toggle button's same wait) and while the chat window is open — see
     syncLauncherVisibility() in BotWidget.ts, which is also where the badge count
     that used to sit on the circular button now attaches to the primary pill.
     Deliberately NOT position:fixed with its own hardcoded bottom offset — it used
     to be, tuned to clear the old, much shorter greeting bubble, and silently
     started overlapping the bubble's corner once the card grew taller. Flowing it
     as a normal sibling of .bot-greeting-widget/.ai-assistant-card inside
     .bot-widget-container (itself the fixed, bottom-right-anchored, shrink-to-fit
     anchor) lets the gap self-adjust to whatever height the card actually renders
     at — 1 line, multiple quick questions, or no card at all. */
  .bot-launcher-group {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transform: translateY(8px);
    transition:
      opacity 0.25s ease-in-out,
      transform 0.25s ease-in-out,
      visibility 0s linear 0.25s;
  }

  .bot-launcher-group.show {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transform: translateY(0);
    transition:
      opacity 0.25s ease-in-out,
      transform 0.25s ease-in-out;
  }

  .bot-launcher-btn {
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    border: none;
    border-radius: 30px;
    height: 36px;
    padding: 0 12px;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    cursor: pointer;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18);
    transition: transform 200ms ease-in-out;
  }

  .bot-launcher-btn:hover {
    transform: translateY(-2px);
  }

  /* First-appearance intro (BotWidget.syncLauncherVisibility adds .intro once per
     page load). Kept inside the pill — the pill itself just fades in as usual:
       - a gradient ring (the greeting ask field's conic sweep, .bot-greeting-ask
         ::after) fades in on the pill's edge, circles it twice and fades out;
       - the two sparkle stars twinkle one after the other;
       - a soft shine sweeps across once, mid-way through.
     ::before/::after are inset 0 and clipped to the pill's rounded box, so the
     button itself needn't be overflow:hidden (that would clip the unread badge). */
  @property --bot-launcher-ring-angle {
    syntax: '<angle>';
    inherits: false;
    initial-value: 0deg;
  }

  .bot-launcher-primary.intro .bot-launcher-icon-star {
    transform-origin: center;
    animation: botLauncherTwinkle 0.7s ease-in-out 0.4s 2;
  }
  .bot-launcher-primary.intro .bot-launcher-icon-star-small {
    animation-delay: 0.55s;
  }
  .bot-launcher-primary.intro::after {
    content: '';
    position: absolute;
    inset: 0;
    padding: 1.5px;
    border-radius: inherit;
    pointer-events: none;
    background: conic-gradient(
      from var(--bot-launcher-ring-angle),
      color-mix(in srgb, ${ringStart} 0%, transparent) 0deg,
      ${ringStart} 60deg,
      ${ringMid} 120deg,
      ${ringEnd} 185deg,
      color-mix(in srgb, ${ringEnd} 0%, transparent) 250deg,
      color-mix(in srgb, ${ringStart} 0%, transparent) 360deg
    );
    -webkit-mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    mask-composite: exclude;
    opacity: 0;
    animation: botLauncherRing 3s linear 0.2s;
  }
  .bot-launcher-primary.intro::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    background: linear-gradient(110deg, transparent 35%, rgba(255, 255, 255, 0.22) 50%, transparent 65%)
      no-repeat;
    background-size: 250% 100%;
    background-position: 150% 0;
    animation: botLauncherShine 1.1s ease-in-out 1.2s;
  }

  @keyframes botLauncherRing {
    0% {
      opacity: 0;
      --bot-launcher-ring-angle: 0deg;
    }
    12%,
    85% {
      opacity: 1;
    }
    100% {
      opacity: 0;
      --bot-launcher-ring-angle: 720deg;
    }
  }

  @keyframes botLauncherTwinkle {
    0%,
    100% {
      transform: scale(1) rotate(0deg);
    }
    50% {
      transform: scale(1.3) rotate(90deg);
    }
  }

  @keyframes botLauncherShine {
    from {
      background-position: 150% 0;
    }
    to {
      background-position: -50% 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .bot-launcher-primary.intro .bot-launcher-icon-star,
    .bot-launcher-primary.intro::before,
    .bot-launcher-primary.intro::after {
      animation: none;
    }
  }

  .bot-launcher-icon {
    position: relative;
    flex: none;
    width: 16px;
    height: 16px;
  }

  .bot-launcher-icon-star {
    position: absolute;
    display: block;
  }

  /* CSS fill overrides the SVGs' own fill attribute (see icons.ts). */
  .bot-launcher-icon-star-large {
    top: 0;
    left: 6px;
    width: 13px;
    height: 13px;
    fill: ${primaryColor};
  }

  .bot-launcher-icon-star-small {
    top: 8px;
    right: 6px;
    width: 8px;
    height: 8px;
    fill: ${secondaryColor};
  }

  .bot-launcher-primary {
    background: #17191e;
    color: #fff;
  }

  .bot-launcher-secondary {
    background: #fff;
    color: #17191e;
    border: 1px solid rgba(0, 0, 0, 0.12);
  }

  .bot-launcher-group.disabled .bot-launcher-primary,
  .bot-launcher-group.disabled .bot-launcher-secondary {
    background: #ccc;
    color: #666;
    cursor: not-allowed;
    box-shadow: none;
  }

  /* Keep each pill's own background on hover — host pages often style button:hover. */
  .bot-launcher-primary:hover {
    background: #17191e !important;
  }

  .bot-launcher-secondary:hover {
    background: #fff !important;
  }

  .bot-launcher-group.disabled .bot-launcher-primary:hover,
  .bot-launcher-group.disabled .bot-launcher-secondary:hover {
    background: #ccc !important;
  }
${buildLauncherOverrides(config.launcherStyle)}

  /* Chat window dynamic styles. Every geometry/transform/fade change runs on the
     same timing as the floating ⇄ sidebar morph and the host page's body padding
     (VIEW_MODE_MORPH_MS/_EASING) — the sidebar slides in while syncBodyPadding()
     pushes the page aside, and on different curves the page visibly lags behind
     the panel and then catches up. will-change keeps the window on its own
     compositor layer so its slide stays smooth while the host page re-lays out
     underneath it. */
  .bot-chat-window {
    position: fixed;
    background: white;
    will-change: transform, opacity;
    transition:
      ${windowTransitions},
      /* Closing: stay rendered (visible) for the full fade/slide-out below,
         only flipping to hidden once it's actually finished — without this delay
         visibility snaps to hidden on the same frame the 'open' class is removed
         and the close looks like an instant cut, not a transition. */
      visibility 0s linear ${VIEW_MODE_MORPH_MS}ms;
    visibility: hidden;
    opacity: 0;
    z-index: 999999;
  }

  .bot-chat-window.open {
    visibility: visible;
    opacity: 1;
    /* Opening: become visible on the same frame the fade/slide-in starts, no delay. */
    transition: ${windowTransitions};
  }



  /* Floating Overlay Mode — a compact Intercom-style card fixed in the bottom-right
     corner, sitting on top of the page without displacing its content. Driven by the
     ToggleFloatingChat message from the iframe header; the mode itself (and its
     persistence) is owned entirely here on the parent page — see setViewMode() in
     BotWidget.ts — since the iframe can't resize or reposition its own frame. */
  .bot-chat-window.floating-mode {
    top: auto;
    left: auto;
    bottom: 60px;
    right: 20px;
    /* Minus both 20px gutters — a plain 100% plus right: 20px overflowed the
       opposite edge on any screen narrower than max-width + 40px. */
    width: calc(100% - 40px);
    height: calc(100% - 80px);
    max-width: 420px;
    max-height: 720px;
    border-radius: 16px ;
    box-shadow: 0 2px 35px #0003 ;
    transform: translateY(16px) scale(0.96);
  }
  .bot-chat-window.floating-mode.open {
    transform: translateY(0) scale(1);
  }
  .bot-chat-window.floating-mode .bot-chat-iframe {
    border-radius: 16px;
  }

  /* config.chatWidgetPosition (home sites only — see BotWidget.createDom()). The
     default bottom-right needs no class; the card keeps its size either way, and its
     Close pill below follows it to the same side. */
  .bot-chat-window.floating-mode.chat-pos-bottom-left,
  .bot-chat-window.floating-mode.chat-pos-top-left {
    left: 20px;
    right: auto;
  }
  .bot-chat-window.floating-mode.chat-pos-top-right,
  .bot-chat-window.floating-mode.chat-pos-top-left {
    top: 20px;
    bottom: auto;
    transform: translateY(-16px) scale(0.96);
  }
  .bot-chat-window.floating-mode.chat-pos-top-right.open,
  .bot-chat-window.floating-mode.chat-pos-top-left.open {
    transform: translateY(0) scale(1);
  }

  /* The dedicated "Close" pill for Floating Overlay Mode — the header itself has no
     close icon (see ChatBotHeader.tsx in chatbot-ui), so this is the only way to
     close a floating chat. Toggled visible only in floating mode while the chat is
     open — see syncFloatingCloseButton() in BotWidget.ts. Sits low in the same
     bottom-right corner as the floating card, just outside its footprint. */
  .bot-floating-close-btn {
    display: flex;
    position: fixed;
    bottom: 12px;
    right: 24px;
    z-index: 1000000;
    align-items: center;
    gap: 6px;
    background: #1a1a1a;
    color: #fff;
    border: none;
    border-radius: 999px;
    padding: 8px 16px 8px 14px;
    font-size: 13px;
    font-weight: 500;
    font-family: inherit;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transform: translateY(6px);
    transition:
      opacity 0.2s ease-in-out,
      transform 0.2s ease-in-out,
      visibility 0s linear 0.2s;
  }

  .bot-floating-close-btn.chat-pos-bottom-left,
  .bot-floating-close-btn.chat-pos-top-left {
    left: 24px;
    right: auto;
  }

  .bot-floating-close-btn:hover {
    background: #333;
  }

  .bot-floating-close-btn.show {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transform: translateY(0);
    transition:
      opacity 0.2s ease-in-out,
      transform 0.2s ease-in-out;
  }

  /* Sidebar Panel Mode — full-height, flush against the right edge, sharp corners,
     sliding in from off-screen right rather than just appearing. This is the
     widget's long-standing default look (matches the isSecondVersion branch
     above); this class exists so the toggle can return to it explicitly and
     predictably regardless of which config drove the initial docked styles.
     Pushes the host page's own content over via body padding-right instead of
     sitting on top of it — see syncBodyPadding() in BotWidget.ts. */
  .bot-chat-window.sidebar-mode {
    top: 0 ;
    left: initial ;
    bottom: initial ;
    right: 0 ;
    width: ${SIDEBAR_WIDTH_PX}px ;
    height: 100vh ;
    max-width: ${SIDEBAR_WIDTH_PX}px ;
    max-height: 100vh ;
    min-height: 100vh ;
    border-radius: 0 ;
    border: none;
    transform: translateX(100%);
  }

  .bot-chat-window.sidebar-mode.open {
    transform: translateX(0);
  }

  .bot-chat-window.sidebar-mode .bot-chat-iframe {
    border-radius: 0;
  }

  /* Set only while BotWidget.morphViewMode() runs its floating ⇄ sidebar animation.
     The two modes anchor to different edges (top: auto vs top: 0), which CSS
     transitions can't interpolate, so the morph animates explicit pixel geometry
     instead — and CSS transitions outrank animations in the cascade, so they're
     switched off here or they'd fight it. */
  .bot-chat-window.view-morphing,
  .bot-chat-window.view-morphing .bot-chat-iframe {
    transition: none !important;
  }

  /* Both of these must come *after* (and outrank) floating-mode/sidebar-mode above:
     those two are always-on state classes now, and their dimensions are !important,
     so a plain (non-important) rule here would never win — the AIChatBot header's
     Maximize/Collapse toggle would silently do nothing. !important + later source
     order is what makes "maximize" override whichever view mode is active.
     Width only, on purpose (X axis, not Y) — matches the old script's own naming
     ("maxmimumWidth"/config.maximumWidth). right stays pinned from whichever view
     mode is active, so growing the width grows the box leftward, not the height. */
  .bot-chat-window.maxmimumWidth {
    width: ${config.maximumWidth}px !important;
    max-width: ${config.maximumWidth}px !important;
  }

  /* Phones: the floating card fills the screen with a small inset on every side,
     whichever corner it's configured for, and "maximize" can't push it wider than
     the screen. */
  @media (max-width: 480px) {
    .bot-chat-window.floating-mode,
    .bot-chat-window.floating-mode[class*='chat-pos-'] {
      top: 8px;
      /* Leaves room underneath for the floating Close pill (bottom: 12px). */
      bottom: 56px;
      left: 8px;
      right: 8px;
      width: auto;
      height: auto;
      max-width: none;
      max-height: none;
    }
    .bot-chat-window.maxmimumWidth {
      width: auto !important;
      max-width: none !important;
    }
  }

  .bot-chat-iframe {
    width: 100% !important;
    height: 100%;
    border: none;
    ${isSecondVersion ? 'border-radius: 0;' : 'border-radius: 20px;'}
  }

  .bot-error-message {
    position: fixed;
    bottom: 120px;
    right: 20px;
    background: #ff4444;
    color: white;
    padding: 10px;
    border-radius: 5px;
    display: block;
    max-width: 250px;
    text-align: center;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transform: translateY(6px);
    transition:
      opacity 0.25s ease-in-out,
      transform 0.25s ease-in-out,
      visibility 0s linear 0.25s;
  }

  .bot-error-message.show {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transform: translateY(0);
    transition:
      opacity 0.25s ease-in-out,
      transform 0.25s ease-in-out;
  }

  /* Host pages can set aggressive global text styles (color/font/weight resets) —
     this keeps the greeting card's own look regardless of what a host page's global
     button/heading/paragraph rules do. */
  .bot-greeting-widget, .bot-greeting-widget * {
    font-family: inherit !important;
  }

  /* One unified card (was two separately-floating bubbles: the message, and a
     second box below it for the quick-question buttons) — a single surface with an
     internal header + actions section, matching the target design directly rather
     than stacking two bubbles. */
  .bot-greeting {
    position: relative;
    width: 340px;
    max-width: calc(100vw - 40px);
    /* Brand glow — a soft primary wash from the top-left and secondary from the
       top-right, both fading out before the questions, on a white card. */
    background:
      radial-gradient(120% 70% at 0% 0%, color-mix(in srgb, ${primaryColor} 10%, transparent) 0%, transparent 60%),
      radial-gradient(120% 70% at 100% 0%, color-mix(in srgb, ${secondaryColor} 12%, transparent) 0%, transparent 60%),
      #fff;
    border: 1px solid rgba(23, 25, 30, 0.06);
    border-radius: 20px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
    overflow: hidden;
    margin-bottom: 12px;
    animation: slideIn 0.3s ease-out;
    transform-origin: bottom right;
  }

  /* Speech-bubble tail pointing down at the "Ask Bandit" pill. Lives on the
     wrapper rather than .bot-greeting, whose overflow: hidden would clip it; it
     sits in the card's 12px bottom margin and overlaps the card's bottom border so
     the two read as one shape. Flips side/direction with the launcher anchor. */
  .bot-greeting-widget {
    position: relative;
  }
  .bot-greeting-widget::after {
    content: '';
    position: absolute;
    bottom: 5px;
    right: 32px;
    width: 14px;
    height: 14px;
    background: #fff;
    border-right: 1px solid rgba(23, 25, 30, 0.06);
    border-bottom: 1px solid rgba(23, 25, 30, 0.06);
    box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.08);
    transform: rotate(45deg);
    pointer-events: none;
  }
  .bot-widget-container[data-anchor-x="left"] .bot-greeting-widget::after {
    right: auto;
    left: 32px;
  }
  .bot-widget-container[data-anchor-y="top"] .bot-greeting-widget::after {
    bottom: auto;
    top: 9px;
    transform: rotate(225deg);
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(10px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  /* Every section shares the same 20px side inset so the avatar, label, chips and
     input all start on one left edge. */
  .bot-greeting-header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 20px 52px 18px 20px;
  }

  .bot-greeting-copy {
    min-width: 0;
  }

  .bot-greeting-title {
    margin: 0 0 6px;
    color: #17191e !important;
    font-size: 17px;
    line-height: 1.3;
    font-weight: 700 !important;
    letter-spacing: -0.01em;
  }

  .bot-greeting-text {
    margin: 0;
    color: #5d687c !important;
    font-size: 13px !important;
    line-height: 1.5 !important;
    font-weight: 400 !important;
  }

  /* Size and shape are !important (here and on .bot-greeting-ask-send) so host-page
     button rules — padding, min-height, line-height — can't stretch the disc into
     a pill. */
  .bot-greeting-close {
    position: absolute;
    top: 14px;
    right: 14px;
    box-sizing: border-box !important;
    width: 28px !important;
    height: 28px !important;
    min-width: 28px !important;
    min-height: 28px !important;
    max-width: 28px !important;
    max-height: 28px !important;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 0 !important;
    border-radius: 50% !important;
    background: transparent !important;
    color: #8a93a3 !important;
    font-size: 18px;
    line-height: 1 !important;
    padding: 0 !important;
    margin: 0 !important;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease;
  }

  .bot-greeting-close:hover {
    background: #f1f3f6 !important;
    color: #17191e !important;
  }

  .bot-greeting-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0 20px 12px;
  }

  .bot-greeting-label {
    margin: 0 0 4px;
    color: #8a93a3 !important;
    font-size: 11px;
    line-height: 1.4;
    font-weight: 600 !important;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  /* Quick questions are soft tinted full-width chips; "Chat with me" is the
     primary action, in the launcher pill's dark #17191e. */
  .bot-quick-question {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 11px 16px;
    border: 1px solid transparent;
    /* Half the one-line chip height — a full pill on one line, but a rounded
       rectangle (not a stretched capsule) when a long question wraps. */
    border-radius: 21px;
    /* Slightly see-through so the brand glow tints a chip that sits under it. */
    background: rgba(244, 245, 247, 0.85);
    color: #17191e !important;
    font-size: 13px;
    line-height: 1.4;
    font-weight: 500 !important;
    text-align: left;
    cursor: pointer;
    transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
  }

  /* Trailing chevron — drawn in CSS so the question markup stays plain text. */
  .bot-quick-question::after {
    content: '';
    flex: none;
    width: 6px;
    height: 6px;
    margin-right: 2px;
    border-top: 1.5px solid #8a93a3;
    border-right: 1.5px solid #8a93a3;
    transform: rotate(45deg);
    transition: border-color 0.2s ease, transform 0.2s ease;
  }

  /* Hover lifts the chip to white with a primary-tinted edge and glow, and the
     chevron takes the primary color as it slides forward. */
  .bot-quick-question:hover {
    background: #fff;
    border-color: color-mix(in srgb, ${primaryColor} 35%, transparent);
    box-shadow: 0 2px 8px color-mix(in srgb, ${primaryColor} 10%, transparent);
  }

  .bot-quick-question:active {
    background: color-mix(in srgb, ${primaryColor} 6%, #fff);
  }

  .bot-quick-question:hover::after {
    border-color: ${primaryColor};
    transform: translateX(3px) rotate(45deg);
  }

  /* Wrapper for the "Or type your question" field — same white surface as the
     rest of the card; spacing alone sets it apart from the chips. */
  .bot-greeting-footer {
    padding: 4px 20px 20px;
  }

  /* A rounded white field with the send button tucked inside it, matching the
     chat composer; the outline (vs the chips' grey fill) keeps it from reading
     as another chip. */
  .bot-greeting-ask {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    padding: 5px 5px 5px 16px;
    border: 1px solid rgba(23, 25, 30, 0.12);
    border-radius: 999px;
    background: #fff;
    box-shadow: 0 1px 2px rgba(23, 25, 30, 0.04);
    transition: border-color 0.25s ease, box-shadow 0.25s ease;
  }

  /* While the ring is showing, the grey border recedes so the sweep reads as the
     field's edge rather than a second outline on top of it; on focus a soft
     brand halo is added under it. */
  .bot-greeting-ask.intro,
  .bot-greeting-ask:focus-within {
    border-color: rgba(23, 25, 30, 0.06);
  }

  .bot-greeting-ask:focus-within {
    box-shadow: 0 0 0 4px color-mix(in srgb, ${secondaryColor} 12%, transparent);
  }

  /* Animated gradient ring — the same rotating conic sweep as the chat composer's
     .chat-input-ring in chatbot-ui's globals.css, shown on focus and for the
     first 5s after the greeting appears (.intro, toggled in setupGreeting()).
     The angle needs @property to animate smoothly inside conic-gradient(). */
  @property --bot-greeting-ring-angle {
    syntax: '<angle>';
    inherits: false;
    initial-value: 0deg;
  }

  @keyframes botGreetingRingRotate {
    to {
      --bot-greeting-ring-angle: 360deg;
    }
  }

  .bot-greeting-ask::after {
    content: '';
    position: absolute;
    inset: -1px;
    padding: 1.5px;
    border-radius: inherit;
    background: conic-gradient(
      from var(--bot-greeting-ring-angle),
      color-mix(in srgb, ${ringStart} 0%, transparent) 0deg,
      ${ringStart} 60deg,
      ${ringMid} 120deg,
      ${ringEnd} 185deg,
      color-mix(in srgb, ${ringEnd} 0%, transparent) 250deg,
      color-mix(in srgb, ${ringStart} 0%, transparent) 360deg
    );
    -webkit-mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    mask-composite: exclude;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.25s;
  }

  .bot-greeting-ask:focus-within::after,
  .bot-greeting-ask.intro::after {
    opacity: 1;
    animation: botGreetingRingRotate 2.6s linear infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .bot-greeting-ask:focus-within::after,
    .bot-greeting-ask.intro::after {
      animation: none;
    }
  }

  .bot-greeting-ask-input {
    flex: 1;
    min-width: 0;
    height: 30px;
    padding: 0;
    border: 0 !important;
    outline: none !important;
    box-shadow: none !important;
    background: transparent !important;
    color: #17191e !important;
    font-size: 13px;
    font-weight: 500 !important;
  }

  .bot-greeting-ask-input::placeholder {
    color: #8a93a3;
    font-weight: 400;
  }

  .bot-greeting-ask-send {
    flex: none;
    box-sizing: border-box !important;
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    min-height: 32px !important;
    max-width: 32px !important;
    max-height: 32px !important;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 !important;
    margin: 0 !important;
    border: 0 !important;
    border-radius: 50% !important;
    line-height: 1 !important;
    background: #17191e !important;
    color: #fff !important;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease;
  }

  .bot-greeting-ask-send:hover:not(:disabled) {
    background: ${primaryColor} !important;
  }

  /* Empty input — a flat grey disc rather than a faded black one, and !important so
     host-page button / button:disabled rules can't make it look clickable. */
  .bot-greeting-ask-send:disabled {
    background: #e4e7ec !important;
    color: #a3abb8 !important;
    opacity: 1;
    cursor: not-allowed;
  }

  .bot-quick-question:focus-visible, .bot-greeting-close:focus-visible,
  .bot-greeting-ask-send:focus-visible {
    outline: 2px solid ${secondaryColor};
    outline-offset: 2px;
  }

  @media (max-width: 480px) {
    .bot-greeting { width: calc(100vw - 32px); }
    .bot-greeting-header { padding: 18px 48px 16px 16px; }
    .bot-greeting-title { font-size: 15px; }
    .bot-greeting-actions { padding: 0 16px 14px; }
    .bot-greeting-footer { padding: 4px 16px 16px; }
  }

  .bot-badge {
    position: absolute;
    top: -16px;
    right: -2px;
    background: #CA140B;
    color: white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    visibility: hidden;
    transform: scale(0.7);
    transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out, visibility 0s linear 0.2s;
}

  .bot-badge.show {
    opacity: 1;
    visibility: visible;
    transform: scale(1);
    transition:
      opacity 0.2s ease-in-out,
      transform 0.2s ease-in-out;
  }

  /* display:none stays the resting state here (rather than visibility, like the
     fixed/absolute overlays above) since this one shares normal document flow with
     .ai-assistant-card below it — see BotWidget.ts's setFadeVisible, which flips
     display briefly around the .show transition instead of leaving it always laid
     out (and clickable) even while faded to invisible. */
  .bot-greeting-widget {
    display: none;
    opacity: 0;
    transform: translateY(8px);
    transition:
      opacity 0.25s ease-in-out,
      transform 0.25s ease-in-out;
  }

  .bot-greeting-widget.show {
    opacity: 1;
    transform: translateY(0);
  }

  .application-error-ans {
    position: fixed;
    bottom: 90px;
    left: 20px;
    background: white;
    padding: 15px;
    border-radius: 8px;
    display: block;
    max-width: 250px;
    text-align: center;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    border: 1px solid #e0e0e0;
    color: #333;
    font-size: 14px;
    line-height: 1.4;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transform: translateY(6px);
    transition:
      opacity 0.3s ease,
      transform 0.3s ease,
      bottom 0.3s ease,
      visibility 0s linear 0.3s;
  }

  .application-error-ans::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 24px;
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid white;
  }

  .application-error-ans.show {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transform: translateY(0);
    transition:
      opacity 0.3s ease,
      transform 0.3s ease,
      bottom 0.3s ease;
  }

  .close-icon {
    position: absolute;
    top: 8px;
    right: 8px;
    background: rgba(238, 238, 238, 0.4);
    border-radius: 1000px;
    border: none;
    color: #666;
    cursor: pointer;
    padding: 4px;
    font-size: 11px;
    font-weight: 500;
    line-height: 1;
  }

  .close-icon:hover {
    color: #333;
  }

  .error-ans {
    padding-right: 20px;
  }

    .ai-assistant-card {
      display: none;
      background: white;
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      width: 320px;
      margin-top: 16px;
      position: relative;
      opacity: 0;
      transform: translateY(8px) scale(0.98);
      transition:
        opacity 0.25s ease-in-out,
        transform 0.25s ease-in-out;
    }

    .ai-assistant-card.show {
      opacity: 1;
      transform: translateY(0) scale(1);
    }

    .ai-assistant-content {
      padding: 24px;
      text-align: center;
    }

    .ai-icon {
      width: 48px;
      height: 48px;
      background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 16px;
      color: white;
      font-weight: bold;
      font-size: 20px;
    }

    .widget-title {
      font-size: 24px;
      font-weight: 700;
      color: #1a202c;
      margin-bottom: 8px;
    }

    .widget-subtitle {
      font-size: 18px;
      color: #2d3748;
      margin-bottom: 16px;
    }

    .widget-description {
      font-size: 14px;
      color: #718096;
      line-height: 1.5;
      margin-bottom: 24px;
    }

    .ai-try-now-btn {
      background: ${isTaxBandits ? 'linear-gradient(90deg, #4a90e2 0%, #ff6b35 100%)' : '#4a90e2'};
      color: white;
      border: none;
      padding: 14px 32px;
      border-radius: 25px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      width: 100%;
      transition: transform 0.2s;
    }

    .ai-try-now-btn:hover {
      transform: scale(1.02);
    }

    .ai-close-icon {
    position: absolute;
    top: 12px;
    right: 12px;
    background: rgba(0, 0, 0, 0.1);
    border: none;
    border-radius: 50%;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 24px;
    color: #fff;
    transition: background 0.2s;
    z-index: 10;
  }

  .ai-close-icon:hover {
    background: rgba(0, 0, 0, 0.2);
  }
`
}

// Ported 1:1 from createQuickHelpWidget()'s inline <style> block.
export const QUICK_HELP_STYLES = `
  .quick-help-bot-chat-window {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 400px;
    max-width: 100%;
    height: 100vh;
    background: #fff;
    box-shadow: -4px 0 12px rgba(0,0,0,0.2);
    z-index: 999999;
    visibility: hidden;
    transform: translateX(100%);
    /* Closing: stay rendered for the full slide-out before flipping to hidden — same
       delayed-visibility technique as .bot-chat-window in buildWidgetStyles, so the
       close animates instead of cutting instantly. */
    transition: transform 0.35s ease-in-out, visibility 0s linear 0.35s;
  }

  .quick-help-avatar img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  .quick-help-bot-chat-window.open {
    visibility: visible;
    transform: translateX(0);
    transition: transform 0.35s ease-in-out;
  }

  @media (min-width: 768px) and (max-width: 1440px) {
    .quick-help-bot-chat-window {
      height: calc(100vh - 57px);
      bottom: 10px;
      right: 20px;
      border-radius: 12px;
      width: 380px;
    }
  }

  @media (min-width: 1441px) {
    .quick-help-bot-chat-window {
      height: calc(100vh - 57px);
      bottom: 20px;
      right: 420px;
      border-radius: 12px;
      width: 420px;
    }
  }

  .quick-help-bot-chat-header {
    background: #fff;
    padding: 16px 20px;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
  }

  .quick-help-header-info {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .quick-help-avatar {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 12px;
    font-weight: 500;
  }

  .quick-help-title {
    font-size: 16px;
    font-weight: 500;
    color: #374151;
    margin: 0;
  }

  .quick-help-close {
    width: 20px;
    height: 20px;
    border: none;
    background: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9ca3af;
    font-size: 18px;
    line-height: 1;
  }

  .quick-help-close:hover {
    color: #6b7280;
  }

  .quick-help-bot-chat-iframe {
    width: 100%;
    height: 100%;
    border: none;
    flex: 1;
  }

  @media (max-width: 480px) {
    .quick-help-bot-chat-window {
      right: 10px;
      bottom: 10px;
      left: 10px;
      width: auto;
      height: calc(100vh - 20px);
      border-radius: 8px;
    }
  }
`
